/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/no-danger */
import React, { ReactNode, useEffect, useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useHTTPError } from 'br2s-ui-common-utils-02';
import {
  Button,
  Datepicker,
  DropdownMenu,
  FileInfo,
  FileUploader,
  InlineAlert,
  InlineStateIndicator,
  RadioButtonField,
  TextArea,
  TextField,
  TextFieldInput,
} from 'br2s-ui-componentlibrary_r2_02';
import {
  INITIAL_FORM_VALUE,
  FULL_NAME_REGEX,
  UEN_NUMBER_REGEX,
  CONTACT_NUMBER_REGEX,
  EMAIL_REGEX,
  NO_SPECIAL_CHARS_REGEX,
  TOPIC_OPTIONS,
  TOPIC_INFO,
  SALUTATION_OPTIONS,
  ROLE_OPTIONS,
  ENTITY_TYPE_OPTIONS,
  CONTACT_NUMBER_TYPE_OPTIONS,
  FEEDBACK_INPUT_ERROR,
} from './../crmConstants';
import { CRM_FORM } from '@/services/api/crm/crm';
import useLoadCaptcha from '@/hooks/useLoadCaptcha';
import {
  generateSession,
  isAttachmentsLoadingOrError,
  replaceEmptyStringWithNull,
  formatDate as format,
} from '../crmUtilities/crmUtils';

import './crm-feedback-form.scss';
import '../crm-form.scss';
import { UploadService } from 'br2s-ui-common-utils-02';

type FormError = {
  [key: string]: string;
};

type Alert = {
  type: 'error' | 'success' | 'informative' | 'warning';
  message: ReactNode;
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

const CRMFeedbackForm = () => {
  useLoadCaptcha();
  const isFirstRender = useRef(true);
  const { handleHTTPError } = useHTTPError();
  const [formValue, setFormValue] = useState(INITIAL_FORM_VALUE);
  const [attachments, setAttachments] = useState<FileInfo[]>([]);
  const [formError, setFormError] = useState<FormError>({});
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [alert, setAlert] = useState<Alert>({
    type: 'informative',
    message: '',
  });

  useEffect(() => {
    if (isFirstRender.current) {
      fetchSession();
      isFirstRender.current = false;
      window.sessionStorage.setItem('transactionId', uuidv4());
      return () => window.sessionStorage.removeItem('transactionId');
    }
  }, []);

  // istanbul ignore next
  const fetchSession = async () => {
    try {
      await generateSession();
    } catch (error) {
      console.error(error);
      handleHTTPError(error, {
        errorType: 'inline',
      });
    }
  };

  const isOfficeNumberTypeSelected =
    formValue.contactNumberType === 'Office Number';

  const handleInputChange = (key: string, val: string) => {
    setFormValue((prevState) => ({ ...prevState, [key]: val }));
  };

  const removeError = (key: string) => {
    setFormError((prevState) => {
      const temp = { ...prevState };
      delete temp[key];
      return temp;
    });
  };

  const handleInputChangeAndRemoveError = (key: string, val: string) => {
    setFormValue((prevState) => ({ ...prevState, [key]: val }));
    removeError(key);
  };

  // set the empty error to alert.
  const setFormEmptyError = () => {
    if (!alert.message) {
      setAlert({
        type: 'error',
        message: 'Please fill in the required fields',
      });
    }
  };

  // istanbul ignore next
  const validateFullName = (salutationVal: string, fullNameVal: string) => {
    let isValid = true;
    if (fullNameVal === '') {
      setFormError((prevState) => ({
        ...prevState,
        fullName: 'required',
      }));
      setFormEmptyError();
      isValid = false;
    } else if (salutationVal === '') {
      setFormError((prevState) => ({
        ...prevState,
        fullName: 'salutationRequired',
      }));
      setFormEmptyError();
      isValid = false;
    } else {
      removeError('fullName');
    }
    return isValid;
  };

  const validateUenNumber = (val: string) => {
    let isValid = true;
    if (val.length > 0 && val.length < 9) {
      setFormError((prevState) => ({
        ...prevState,
        uenNumber: 'length',
      }));
      isValid = false;
    } else {
      removeError('uenNumber');
    }
    return isValid;
  };

  // istanbul ignore next
  const validateContactNumber = (val: string) => {
    let isValid = true;
    if (val.length === 0) {
      setFormError((prevState) => ({
        ...prevState,
        contactNumber: 'required',
      }));
      setFormEmptyError();
      isValid = false;
    } else if (val.length < 8) {
      setFormError((prevState) => ({
        ...prevState,
        contactNumber: 'minLength',
      }));
      isValid = false;
    } else if (val.length > 8) {
      setFormError((prevState) => ({
        ...prevState,
        contactNumber: 'maxLength',
      }));
      isValid = false;
    } else {
      removeError('contactNumber');
    }
    return isValid;
  };

  // istanbul ignore next
  const validateEmail = (userEmail: string) => {
    let isEmailValid = true;
    if (userEmail === '') {
      setFormError((prevState) => ({
        ...prevState,
        email: 'required',
      }));
      setFormEmptyError();
      isEmailValid = false;
    } else if (!EMAIL_REGEX.test(userEmail)) {
      setFormError((prevState) => ({
        ...prevState,
        email: 'invalid',
      }));
      isEmailValid = false;
    } else {
      removeError('email');
    }
    return isEmailValid;
  };

  //istanbul ignore next
  const validateQuestion = (val: string) => {
    let isValid = true;
    if (val === '') {
      setFormError((prevState) => ({
        ...prevState,
        question: 'required',
      }));
      setFormEmptyError();
      isValid = false;
    } else if (
      !NO_SPECIAL_CHARS_REGEX.test(val) ||
      val.includes('.exe') ||
      val.includes('.sh')
    ) {
      setFormError((prevState) => ({
        ...prevState,
        question: 'specialChars',
      }));
      isValid = false;
    } else {
      removeError('question');
    }
    return isValid;
  };

  //istanbul ignore next
  const getInputState = (key: string) => {
    const status = formError[key];
    if (status !== undefined && status !== '') {
      return 'error';
    }
    if (isSubmitClicked) {
      return 'success';
    }
    return 'default';
  };

  //istanbul ignore next
  const validateForm = () => {
    let isValid = true;
    if (formValue.topic === '') {
      setFormError((prevState) => ({
        ...prevState,
        topic: 'required',
      }));
      setFormEmptyError();
      isValid = false;
    }
    if (!validateFullName(formValue.salutation, formValue.fullName)) {
      isValid = false;
    }
    if (formValue.role === '') {
      setFormError((prevState) => ({
        ...prevState,
        role: 'required',
      }));
      setFormEmptyError();
      isValid = false;
    }
    if (formValue.entityType === '') {
      setFormError((prevState) => ({
        ...prevState,
        entityType: 'required',
      }));
      setFormEmptyError();
      isValid = false;
    }
    if (!validateUenNumber(formValue.uenNumber)) {
      isValid = false;
    }
    if (!validateContactNumber(formValue.contactNumber)) {
      isValid = false;
    }
    if (!validateEmail(formValue.email)) {
      isValid = false;
    }
    if (!validateQuestion(formValue.question)) {
      isValid = false;
    }
    if (isValid) {
      if (alert.message) {
        setAlert({
          type: 'informative',
          message: '',
        });
      }
    }
    return isValid;
  };

  //istanbul ignore next
  const resetForm = () => {
    setFormValue(INITIAL_FORM_VALUE);
    setAttachments([]);
    setFormError({});
    setIsSubmitClicked(false);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    window.sessionStorage.setItem('transactionId', uuidv4());
  };

  //istanbul ignore next
  const getMobileNumber = (val: string) => {
    if (!isOfficeNumberTypeSelected) return val;
    return null;
  };

  //istanbul ignore next
  const getOfficeNumber = (val: string) => {
    if (isOfficeNumberTypeSelected) return val;
    return null;
  };

  //istanbul ignore next
  const getAttachment = () => {
    if (attachments.length) {
      const newAttachmentsList: Record<string, string>[] = [];
      attachments.forEach((attachment) => {
        const newAttachment = {
          '@odata.type': 'Microsoft.Dynamics.CRM.acra_document',
          acra_uuidtext: attachment.successData.uuid,
          acra_filenametext: attachment.successData.name,
        };
        newAttachmentsList.push(newAttachment);
      });
      return newAttachmentsList;
    }
    return null;
  };

  //istanbul ignore next
  const getDate = (val: any) => {
    if (val.dateOfPurchase)
      return format(new Date(val.dateOfPurchase).toString(), {
        format: 'DD.MM.YYYY',
      });
    return null;
  };

  const getTransactId = () =>
    window.sessionStorage.getItem('transactionId') || '';

  //istanbul ignore next
  const generatePayload = () => {
    const constructPayload: any = {
      requestTransactId: getTransactId(),
      subject: formValue.topic,
      salutation: formValue.salutation,
      name: formValue.fullName,
      entityType: formValue.entityType,
      uen: replaceEmptyStringWithNull(formValue.uenNumber),
      contactType: formValue.role,
      mobileNo: getMobileNumber(formValue.contactNumber),
      officeNo: getOfficeNumber(formValue.contactNumber),
      email: formValue.email,
      question: formValue.question,
      attachmentList: getAttachment(),
    };

    if (
      [
        'BizFile Matters',
        'API Subscription / Purchase of Information (iShop)',
      ].includes(formValue.topic)
    ) {
      constructPayload.transactionNo = replaceEmptyStringWithNull(
        formValue.transactionNumber
      );
    }
    if (formValue.topic === 'BizFile Matters') {
      constructPayload.nameAppNo = replaceEmptyStringWithNull(
        formValue.nameApplicationNumber
      );
    }
    if (
      [
        'Approved Refund/Payment Status',
        'API Subscription / Purchase of Information (iShop)',
      ].includes(formValue.topic)
    ) {
      constructPayload.receiptNoArn = replaceEmptyStringWithNull(formValue.arn);
    }
    if (formValue.topic === 'Approved Refund/Payment Status') {
      constructPayload.refundAppNo = replaceEmptyStringWithNull(
        formValue.refundApplicationNumber
      );
      constructPayload.depositAccNo = replaceEmptyStringWithNull(
        formValue.depositAccountNumber
      );
      constructPayload.filingAgentNo = replaceEmptyStringWithNull(
        formValue.fillingAgentNumber
      );
    }
    if (
      formValue.topic === 'API Subscription / Purchase of Information (iShop)'
    ) {
      constructPayload.purchaseRequestDate = getDate(formValue);
    }
    return constructPayload;
  };

  //istanbul ignore next
  const handleSubmit = async () => {
    setIsSubmitClicked(true);

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    setIsSubmitDisabled(true);

    // @ts-ignore
    window.grecaptcha.ready(() => {
      // @ts-ignore
      window.grecaptcha
        .execute(RECAPTCHA_SITE_KEY, { action: 'submit' })
        .then(async (captchaToken: string) => {
          try {
            const payload = generatePayload();
            const response = await CRM_FORM.submitFeedback(
              payload,
              captchaToken
            );

            if (response?.status === 200) {
              setAlert({
                type: 'success',
                message: 'Success: Your form has been submitted.',
              });
              resetForm();
              setIsSubmitDisabled(false);
            }
          } catch (error: any) {
            console.error(error);
            handleHTTPError(error, {
              errorType: 'inline',
            });
            setIsSubmitDisabled(false);
          }
        });
    });
  };

  //istanbul ignore next
  function showFileUploader() {
    return (
      <FileUploader
        id="attachments"
        label="Attachments (Optional)"
        fileFormatsAccepted={['pdf', 'jpg', 'jpeg', 'png', 'xls', 'xlsx']}
        maxFileSizeInMB={2}
        maxFileNumbers={5}
        files={attachments}
        setFiles={setAttachments}
        handleUpload={(file) => UploadService.handleUpload(file, 'ez')}
        handleDelete={() => undefined}
      />
    );
  }

  return (
    <div className="crm-form-wrapper">
      <div className="container crm-form">
        {alert.message && (
          <InlineAlert
            type={alert.type}
            // @ts-ignore
            headline={alert.message}
          />
        )}

        <DropdownMenu
          id="feedback"
          label="Feedback/Enquiry On"
          options={TOPIC_OPTIONS}
          value={formValue.topic}
          onChange={(val) => handleInputChangeAndRemoveError('topic', val)}
          state={getInputState('topic')}
          helperText={FEEDBACK_INPUT_ERROR.topic[formError.topic] as string}
        />

        {TOPIC_INFO[formValue.topic] && (
          <InlineStateIndicator label={TOPIC_INFO[formValue.topic]} />
        )}

        <DropdownMenu
          id="full_name"
          type="with-text-input"
          label="Full Name"
          options={SALUTATION_OPTIONS}
          value={formValue.salutation}
          placeholder="Select"
          onChange={(val) => {
            handleInputChange('salutation', val);
            removeError('fullName');
          }}
          textInputPlaceholder="Enter your full name"
          textInputValue={formValue.fullName}
          textInputOnChange={(e) => {
            const { value } = e.target;
            // Disallows non alphabet
            if (value !== '' && !FULL_NAME_REGEX.test(value)) {
              setFormError((prevState) => ({
                ...prevState,
                fullName: 'invalid',
              }));
              return;
            }
            handleInputChange('fullName', value);
            if (value === '') {
              setFormError((prevState) => ({
                ...prevState,
                fullName: 'required',
              }));
            } else {
              removeError('fullName');
            }
          }}
          state={getInputState('fullName')}
          helperText={
            FEEDBACK_INPUT_ERROR.fullName[formError.fullName] as string
          }
        />

        <DropdownMenu
          id="role_options"
          label="I am a"
          options={ROLE_OPTIONS}
          value={formValue.role}
          onChange={(val) => handleInputChangeAndRemoveError('role', val)}
          state={getInputState('role')}
          helperText={FEEDBACK_INPUT_ERROR.role[formError.role] as string}
        />

        <DropdownMenu
          id="entity_type_options"
          label="Entity Type"
          options={ENTITY_TYPE_OPTIONS}
          value={formValue.entityType}
          onChange={(val) => handleInputChangeAndRemoveError('entityType', val)}
          state={getInputState('entityType')}
          helperText={
            FEEDBACK_INPUT_ERROR.entityType[formError.entityType] as string
          }
        />

        <TextField
          id="uen-number"
          label="UEN Number (Optional)"
          labelPopupText="UEN should have 9/10 characters"
          state={getInputState('uenNumber')}
        >
          <TextFieldInput
            id="uen-number"
            name="uen-number"
            placeholder="Enter UEN Number if any"
            value={formValue.uenNumber}
            onChange={(e) => {
              const { value } = e.target;
              if (value.length > 10) {
                return;
              }
              // Disallows non numeric / non uppercase alphabet
              if (value !== '' && !UEN_NUMBER_REGEX.test(value)) {
                setFormError((prevState) => ({
                  ...prevState,
                  uenNumber: 'invalid',
                }));
                return;
              }
              handleInputChange('uenNumber', e.target.value);
              validateUenNumber(e.target.value);
            }}
            helperText={
              (FEEDBACK_INPUT_ERROR.uenNumber[formError.uenNumber] as string) ||
              '(UEN number should have 9 or 10 characters, Only Uppercase and numeric characters are allowed.)'
            }
          />
        </TextField>

        <RadioButtonField
          id="radio"
          layout="horizontal"
          label="Contact Number Type"
          options={CONTACT_NUMBER_TYPE_OPTIONS}
          radioButtonValue={formValue.contactNumberType}
          onChange={(val: string) =>
            handleInputChange('contactNumberType', val)
          }
        />
        <div className="crm-feedback-form-inline-form-items">
          <div className="crm-feedback-form-inline-form-items_field-container">
            <TextField
              id="contact-number"
              label={
                isOfficeNumberTypeSelected ? 'Office Number' : 'Mobile Number'
              }
              state={getInputState('contactNumber')}
            >
              <TextFieldInput
                id="contact-number"
                name="contact-number"
                placeholder={
                  isOfficeNumberTypeSelected
                    ? 'Enter your office number'
                    : 'Enter your mobile number'
                }
                value={formValue.contactNumber}
                onChange={(e) => {
                  const { value } = e.target;
                  // Disallows non numeric
                  if (value.length > 0 && !CONTACT_NUMBER_REGEX.test(value)) {
                    return;
                  }
                  handleInputChange('contactNumber', value);
                  validateContactNumber(value);
                }}
                helperText={
                  isOfficeNumberTypeSelected
                    ? (
                        FEEDBACK_INPUT_ERROR.contactNumber[
                          formError.contactNumber
                        ] as any
                      )?.officeNumber
                    : (
                        FEEDBACK_INPUT_ERROR.contactNumber[
                          formError.contactNumber
                        ] as any
                      )?.mobileNumber
                }
              />
            </TextField>
          </div>
          <div className="crm-feedback-form-inline-form-items_field-container">
            <TextField
              id="email"
              label="Email Address"
              state={getInputState('email')}
            >
              <TextFieldInput
                id="email"
                name="email"
                placeholder="Enter a valid email address"
                value={formValue.email}
                onChange={(e) => {
                  handleInputChange('email', e.target.value);
                  validateEmail(e.target.value);
                }}
                helperText={
                  FEEDBACK_INPUT_ERROR.email[formError.email] as string
                }
              />
            </TextField>
          </div>
        </div>
        {[
          'BizFile Matters',
          'API Subscription / Purchase of Information (iShop)',
        ].includes(formValue.topic) && (
          <TextField
            id="transaction-number"
            label="Transaction Number (Optional)"
            state={getInputState('transactionNumber')}
          >
            <TextFieldInput
              id="transaction-number"
              name="transaction-number"
              value={formValue.transactionNumber}
              onChange={(e) =>
                handleInputChange('transactionNumber', e.target.value)
              }
            />
          </TextField>
        )}

        {formValue.topic === 'BizFile Matters' && (
          <TextField
            id="name-application-number"
            label="Name Application Number (Optional)"
            state={getInputState('nameApplicationNumber')}
          >
            <TextFieldInput
              id="name-application-number"
              name="name-application-number"
              value={formValue.nameApplicationNumber}
              onChange={(e) =>
                handleInputChange('nameApplicationNumber', e.target.value)
              }
            />
          </TextField>
        )}

        {[
          'Approved Refund/Payment Status',
          'API Subscription / Purchase of Information (iShop)',
        ].includes(formValue.topic) && (
          <TextField
            id="arn"
            label="Receipt No/ARN (Optional)"
            state={getInputState('arn')}
          >
            <TextFieldInput
              id="arn"
              name="arn"
              value={formValue.arn}
              onChange={(e) => handleInputChange('arn', e.target.value)}
            />
          </TextField>
        )}

        {formValue.topic === 'Approved Refund/Payment Status' && (
          <>
            <TextField
              id="refund-application-number"
              label="Refund Application Number (Optional)"
              state={getInputState('refundApplicationNumber')}
            >
              <TextFieldInput
                id="refund-application-number"
                name="refund-application-number"
                value={formValue.refundApplicationNumber}
                onChange={(e) =>
                  handleInputChange('refundApplicationNumber', e.target.value)
                }
              />
            </TextField>

            <TextField
              id="deposit-account-number"
              label="Deposit Account Number (Optional)"
              state={getInputState('depositAccountNumber')}
            >
              <TextFieldInput
                id="deposit-account-number"
                name="deposit-account-number"
                value={formValue.depositAccountNumber}
                onChange={(e) =>
                  handleInputChange('depositAccountNumber', e.target.value)
                }
              />
            </TextField>

            <TextField
              id="filling-agent-number"
              label="Filing Agent Number (Optional)"
              state={getInputState('fillingAgentNumber')}
            >
              <TextFieldInput
                id="filling-agent-number"
                name="filling-agent-number"
                value={formValue.fillingAgentNumber}
                onChange={(e) =>
                  handleInputChange('fillingAgentNumber', e.target.value)
                }
              />
            </TextField>
          </>
        )}

        {formValue.topic ===
          'API Subscription / Purchase of Information (iShop)' && (
          <Datepicker
            id="date-of-purchase"
            name="date-of-purchase"
            label="Date of Purchase (Optional)"
            maxDate={new Date()}
            value={formValue.dateOfPurchase}
            onChange={(val) => handleInputChange('dateOfPurchase', val)}
            state={getInputState('dateOfPurchase')}
          />
        )}

        <TextArea
          id="question"
          name="question"
          label="Message"
          value={formValue.question}
          onChange={(e) => {
            handleInputChange('question', e.target.value);
            validateQuestion(e.target.value);
          }}
          maxLength={3000}
          state={getInputState('question')}
          helperText={
            FEEDBACK_INPUT_ERROR.question[formError.question] as string
          }
        />

        {showFileUploader()}

        <Button
          id="submit"
          label="Submit"
          onClick={handleSubmit}
          isBlock
          isDisabled={
            isSubmitDisabled || isAttachmentsLoadingOrError(attachments)
          }
        />
      </div>
    </div>
  );
};

export default CRMFeedbackForm;
